import { useCallback, useMemo } from 'react'
import {
	type EmptyProfile,
	type EventType,
	type Profile,
	getEventTypesListEventTypesQueryKey,
	getMeProfileGetMeProfileQueryKey,
	useMeProfileGetMeProfile,
	useMeProfileUpdateMeProfile,
} from '@cocoonspace/api'
import { Switch } from '@cocoonspace/ui/components/switch'
import { useLingui } from '@lingui/react/macro'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

const isProfile = (profile: EmptyProfile | Profile): profile is Profile =>
	!('isEmpty' in profile)

export const ToggleEnabledAction = ({
	eventType,
	isPublic,
}: {
	eventType: EventType
	isPublic: boolean
}) => {
	const { t } = useLingui()
	const queryClient = useQueryClient()

	const { data: meProfile } = useMeProfileGetMeProfile()
	const { mutateAsync } = useMeProfileUpdateMeProfile()

	const publicEventTypeIds = useMemo(() => {
		return meProfile && isProfile(meProfile)
			? (meProfile.eventTypeIds ?? [])
			: []
	}, [meProfile])

	const toggleEnabled = useCallback(
		(nextChecked: boolean) => {
			const current = publicEventTypeIds

			const next = nextChecked
				? Array.from(new Set([...current, eventType.id]))
				: current.filter((id) => id !== eventType.id)

			return mutateAsync({
				data: {
					eventTypeIds: next,
				},
			})
				.then(() => {
					toast.success(t`Type de rendez-vous mis à jour`)

					queryClient.invalidateQueries({
						queryKey: getEventTypesListEventTypesQueryKey(),
					})

					queryClient.invalidateQueries({
						queryKey: getMeProfileGetMeProfileQueryKey(),
					})
				})
				.catch(() => {
					toast.error(t`Une erreur est survenue`)
				})
		},
		[publicEventTypeIds, eventType.id, mutateAsync, t, queryClient],
	)

	return (
		<div className='flex items-center gap-2'>
			<span className='text-muted-foreground text-sm'>
				{isPublic ? t`Publique` : t`Privé`}
			</span>

			<Switch
				checked={isPublic}
				className='ml-auto'
				onCheckedChange={toggleEnabled}
				size='sm'
			/>
		</div>
	)
}
